import { useCallback, useMemo } from 'react';
// @ts-expect-error - @return-rabbit/nexus module declaration is not yet available
import { NexusProvider, RequestProvider, Init, Details } from '@return-rabbit/nexus';
import get from 'lodash.get';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { RequestInitContent, RequestDetailsContent } from './in-progress-return-content';
import { useCurrentUser } from '../../../hooks/use-current-user';
import { useReturnNavigation } from '../hooks/use-return-navigation';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useEntitiesSalesOrder } from '../../../hooks/entities/selectors/sales-orders';
import { usePendingReturnContext } from '../context/pending-return-context-provider';
import { useEnhancedReturnsContext } from '../context/enhanced-returns-context-provider';
import { useEnhancedReturnsAuthToken } from '../../../store/returns/returns.helpers';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';

import { LogService } from '../../../services/log-service';
import { Workflows } from '../../../tracking/tracking-constants';
import { NewEnhancedReturnIdPrefix } from '../hooks/use-pending-return';

type CreateReturnPipelineProps = {
  inProgressId?: string;
};

export function CreateReturnPipeline({ inProgressId }: CreateReturnPipelineProps) {
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? {};
  const orderId = salesOrder?.orderNumber;
  const sellerStoreGuid = salesOrder?.store?.storeGuid;

  const currentUser = useCurrentUser();
  const authToken = useEnhancedReturnsAuthToken();
  const trackCreateReturn = useTrackWorkflow(Workflows.CREATE_RETURN_FLOW);

  const { resetHasPendingReturn } = usePendingReturnContext();
  const { loadEnhancedReturns, registerPendingReturn } = useEnhancedReturnsContext();
  const { navigateToReturn, createPendingEnhancedReturnPlaceholderId } = useReturnNavigation();

  const [localRequestId, setLocalRequestId] = useAsyncSafeState(() => {
    const isPendingReturn = inProgressId?.startsWith(NewEnhancedReturnIdPrefix);
    return isPendingReturn ? inProgressId?.replace(NewEnhancedReturnIdPrefix, '') : undefined;
  });

  const generateError = useCallback(
    (err: object) => {
      trackCreateReturn(WorkflowSteps.ERROR, { requestId: localRequestId });
      // oxlint-disable-next-line no-console
      console.error('[NEXUS] generateError - err', err);
    },
    [localRequestId, trackCreateReturn],
  );

  const onRequestCreated = useCallback(
    ({ requestId }: { requestId: string }) => {
      trackCreateReturn(WorkflowSteps.ITEMS_SELECTED, { requestId });
      setLocalRequestId(requestId);
      registerPendingReturn(requestId);
      const pendingRequestId = createPendingEnhancedReturnPlaceholderId(requestId);
      navigateToReturn(salesOrderId, pendingRequestId, true);
    },
    [setLocalRequestId, trackCreateReturn, registerPendingReturn, navigateToReturn, salesOrderId, createPendingEnhancedReturnPlaceholderId],
  );

  const onRequestConfirmed = useCallback(
    (returnId: string, fulfillmentId: string) => {
      trackCreateReturn(WorkflowSteps.COMPLETE, { requestId: returnId, fulfillmentId });
      setLocalRequestId(undefined);
      resetHasPendingReturn();
      loadEnhancedReturns();
      navigateToReturn(salesOrderId, returnId, true);
    },
    [setLocalRequestId, trackCreateReturn, resetHasPendingReturn, loadEnhancedReturns, navigateToReturn, salesOrderId],
  );

  const segmentApiKey = get(window, 'analytics.settings.writeKey', '');
  const apiBaseURL = get(window, 'SS_GLOBALS.frontEndConfig.ssrApiHost', '');

  const userInfo = useMemo(
    () => ({
      userId: currentUser.userId,
      email: currentUser.email,
      name: currentUser.name,
    }),
    [currentUser.email, currentUser.name, currentUser.userId],
  );

  const nexusAxiosConfig = useMemo(
    () => ({
      baseURL: apiBaseURL,
      headers: {
        Authorization: `Token ${authToken}`,
        'Seller-Store-Guid': sellerStoreGuid,
      },
    }),
    [apiBaseURL, authToken, sellerStoreGuid],
  );

  return (
    <NexusProvider
      axiosConfig={nexusAxiosConfig}
      logError={(err: object) => LogService.logWarning('Failed to initialize NexusProvider', err)}
      segmentApiKey={segmentApiKey}
      userInfo={userInfo}
    >
      <RequestProvider generateError={generateError}>
        {localRequestId ? (
          <Details.Provider requestId={localRequestId}>
            <RequestDetailsContent onRequestConfirmed={onRequestConfirmed} requestId={localRequestId} />
          </Details.Provider>
        ) : (
          <Init.Provider onRequestCreated={onRequestCreated} orderId={orderId}>
            <RequestInitContent />
          </Init.Provider>
        )}
      </RequestProvider>
    </NexusProvider>
  );
}
